import { useReducer, useMemo } from 'react';
import {
  AccountDetail,
  AccountType,
  CommPreference,
  Maybe,
  NotificationFor,
  PremiseInfo,
  Scalars,
} from '../../../__generated__/pge-types';
import { VirtualAccountType } from '../../../constants/customEnum';
import useAccountDetailList from '../../../hooks/useAccountDetailList';
import useAuthQuery from '../../../hooks/useAuthQuery';
import { getAccountsList } from '../../account-dropdown/queries';
import useFeatureFlagProtectedValues from '../../account-summary/multi-accounts/useFeatureFlagProtectedValues';
import accountListReducer from '../../account-summary/multi-accounts/accountListReducer';
import { AccountWithOutageInfo } from '../types';
import { getAccountDetails } from '../queries/queries';
import { useErrorNotification } from '../components/common/error-notification';

type AccountIdentifier = {
  accountNumber?: Scalars['String'];
  encryptedPersonId?: Scalars['String'];
};

type AccountIdentifierList = {
  validAccounts: Array<AccountIdentifier>;
};

const isValidOutageAccount = (account: AccountDetail): boolean =>
  account.isActive &&
  Boolean(account.premiseInfo?.some(p => p?.isEligibleForOutage));

const getValidPremises = (
  premiseInfo?: (PremiseInfo | null)[] | null,
): PremiseInfo[] =>
  (premiseInfo ?? []).filter(
    (p): p is PremiseInfo =>
      !!p &&
      Boolean(p.isEligibleForOutage) &&
      Boolean(p.servicePointDetails?.length),
  );

const getReportedOutageAccountInfo = (
  premiseInfos: PremiseInfo[],
  description: string | undefined,
  isDefault: boolean,
  isActive: boolean,
  accountNumber: string,
  encryptedPersonId: string,
  mainCustomerName: string,
  accountType: AccountType,
  commPreferenceInfo: Maybe<Maybe<CommPreference>[]> | undefined,
  pspsOutage: Maybe<any> | undefined,
): AccountWithOutageInfo => {
  const outagePremise = premiseInfos.find(p =>
    p?.servicePointDetails?.some(point => point?.outageInfo?.hasKnownOutage),
  );

  const commPrefs: CommPreference[] | undefined = commPreferenceInfo
    ? commPreferenceInfo.filter((c): c is CommPreference => !!c)
    : undefined;

  return {
    isActive,
    isPrimaryAccount: isDefault,
    hasKnownOutage: !!outagePremise,
    description: description || accountNumber,
    accountNumber,
    premiseInfos,
    commPreferenceInfo: commPrefs,
    encryptedPersonId,
    addressLocation: outagePremise
      ? outagePremise?.addressLine1 +
        ', ' +
        outagePremise.city +
        ', ' +
        outagePremise.state +
        ' ' +
        outagePremise.postal
      : premiseInfos[0]?.addressLine1 +
        ', ' +
        premiseInfos[0]?.city +
        ', ' +
        premiseInfos[0]?.state +
        ' ' +
        premiseInfos[0]?.postal,
    mainCustomerName,
    accountType,
    pspsOutage: pspsOutage || undefined,
  };
};

export const getAccountOutageInfo = (
  accounts: AccountDetail[],
): AccountWithOutageInfo[] => {
  const validAccounts = accounts.filter(isValidOutageAccount);

  const commercial = validAccounts.filter(
    a => a.accountType !== AccountType.Res,
  );
  const residential = validAccounts.filter(
    a => a.accountType === AccountType.Res,
  );

  const primary = [...commercial, ...residential].filter(a => a.isDefault);

  // build sorted list: primary → non-primary residential → non-primary commercial
  const orderedAccounts = [
    ...(primary ? primary : []),
    ...residential.filter(a => !a.isDefault),
    ...commercial.filter(a => !a.isDefault),
  ];

  return orderedAccounts
    .map(account => {
      const validPremises = getValidPremises(account.premiseInfo);
      return validPremises.length
        ? getReportedOutageAccountInfo(
            validPremises,
            account.description ?? undefined,
            account.isDefault,
            account.isActive,
            account.accountNumber,
            account.encryptedPersonId,
            account.mainCustomerName,
            account.accountType,
            account.commPreferences,
            account.pspsOutage,
          )
        : null;
    })
    .filter((a): a is AccountWithOutageInfo => a !== null);
};

export function useAccountDetailsList() {
  const {
    getAccountListStateByClosedAcctFlag,
  } = useFeatureFlagProtectedValues();
  const { setErrorNotification } = useErrorNotification();

  const [accountListParams] = useReducer(accountListReducer.reducer, {
    ...getAccountListStateByClosedAcctFlag(),
    pageSize: 'All',
  });

  const { accounts } = useAccountDetailList(
    getAccountsList,
    VirtualAccountType.ALL_ACCTS,
    { accountListParams },
  );

  const accountParams = accounts.map(account => ({
    accountNumber: account.accountNumber,
    encryptedPersonId: account.encryptedPersonId,
  }));

  const {
    loading,
    data: accountDetails,
    refetch: refetchAccountList,
    error,
  } = useAuthQuery<{
    getAccountDetails: AccountDetail[];
  }>(getAccountDetails, {
    variables: {
      params: { accountNumberList: accountParams },
      commPreferencesParams: { notificationFor: NotificationFor.Outage },
    },
    skip: !accountParams.length,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const accountListWithOutageInfo = useMemo(() => {
    if (!accountDetails) {
      return [];
    }
    return getAccountOutageInfo(accountDetails.getAccountDetails);
  }, [accountDetails]);

  if (error) {
    setErrorNotification();
  }

  return {
    accountListWithOutageInfo,
    isSingleAccount: accounts.length === 1,
    loading,
    refetchAccountList,
  };
}
